import React, { useState } from 'react';
import {
  Vote,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Calendar,
  Plus,
  BarChart3,
  Sparkles,
} from 'lucide-react';
import { BoardAndReporting } from '../BoardAndReporting';

export const AdminAgmVotingView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'agm_voting' | 'board_discussions'>('agm_voting');

  const agmAgendas = [
    {
      id: 'ag-1',
      agenda_no: 'วาระที่ 1',
      title: 'พิจารณารับรองรายงานการประชุมใหญ่สามัญเจ้าของร่วมประจำปี 2568',
      description: 'รับรองรายงานการประชุมใหญ่ครั้งก่อนหน้า ซึ่งจัดขึ้นเมื่อวันที่ 28 เมษายน 2568',
      total_votes: 118,
      votes_for: 115,
      votes_against: 1,
      votes_abstain: 2,
      status: 'passed',
      quorum_met: true,
    },
    {
      id: 'ag-2',
      agenda_no: 'วาระที่ 2',
      title: 'พิจารณาอนุมัติงบดุลและงบการเงินประจำปี 2568 ซึ่งผ่านการตรวจรับรองโดยผู้สอบบัญชีรับอนุญาต',
      description: 'รายรับค่าส่วนกลางรวม 5,420,000 บาท รายจ่าย 4,180,000 บาท เงินส่วนเกินสะสม 1,240,000 บาท โอนเข้ากองทุนสำรอง',
      total_votes: 120,
      votes_for: 117,
      votes_against: 0,
      votes_abstain: 3,
      status: 'passed',
      quorum_met: true,
    },
    {
      id: 'ag-3',
      agenda_no: 'วาระที่ 3',
      title: 'พิจารณาจัดซื้อและติดตั้งระบบโซล่าร์เซลล์สำหรับอาคารสโมสรคลับเฮาส์ เพื่อลดค่าไฟฟ้าระยะยาว',
      description: 'งบประมาณ 380,000 บาท คาดการณ์คืนทุนภายใน 3.2 ปี ลดค่าไฟฟ้าเฉลี่ยเดือนละ 9,800 บาท',
      total_votes: 112,
      votes_for: 98,
      votes_against: 10,
      votes_abstain: 4,
      status: 'passed',
      quorum_met: true,
    },
    {
      id: 'ag-4',
      agenda_no: 'วาระที่ 4',
      title: 'พิจารณาคัดเลือกบริษัทรักษาความปลอดภัย (รปภ.) ประจำปี 2570',
      description: 'เปรียบเทียบข้อเสนอ 3 บริษัท (บริษัท การ์ดฟอร์ซ, บริษัท สยามซีเคียวริตี้, บริษัท โปรเทคการ์ด)',
      total_votes: 85,
      votes_for: 72,
      votes_against: 8,
      votes_abstain: 5,
      status: 'voting_in_progress',
      quorum_met: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-teal-50 text-teal-600 border border-teal-200">
            <Vote className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              11. งานประชุมใหญ่เจ้าของร่วมและระบบลงคะแนนเสียง (AGM & E-Voting System)
            </h2>
            <p className="text-xs text-slate-500">
              จัดการประชุมใหญ่สามัญประจำปี (AGM), วาระการขอมติ, บันทึกคะแนนเสียง E-Voting และกระดานมติกรรมการ
            </p>
          </div>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-xl text-xs font-semibold text-slate-600">
          <button
            onClick={() => setActiveTab('agm_voting')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'agm_voting' ? 'bg-white text-blue-700 shadow-xs' : 'hover:text-slate-900'
            }`}
          >
            วาระประชุมใหญ่ AGM
          </button>
          <button
            onClick={() => setActiveTab('board_discussions')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'board_discussions' ? 'bg-white text-blue-700 shadow-xs' : 'hover:text-slate-900'
            }`}
          >
            กระดานโหวตมติกรรมการ
          </button>
        </div>
      </div>

      {activeTab === 'agm_voting' ? (
        <div className="space-y-6">
          {/* AGM Summary Banner */}
          <div className="bg-slate-900 text-white p-5 rounded-2xl shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="space-y-1">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30">
                การประชุมใหญ่สามัญประจำปี 2569 (AGM 2026)
              </span>
              <h3 className="font-bold text-base text-white">
                นราวดี อิลิแกนซ์ อยุธยา • องค์ประชุมครบถ้วน (Quorum Met)
              </h3>
              <p className="text-xs text-slate-400">
                ผู้เข้าร่วมประชุม 122 ยูนิต (คิดเป็น 85.9% ของกรรมสิทธิ์ทั้งหมด)
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span className="px-3 py-1.5 bg-teal-600 text-white rounded-xl text-xs font-bold flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> ระบบ E-Voting ทำงานสมบูรณ์
              </span>
            </div>
          </div>

          {/* Agendas List */}
          <div className="space-y-4">
            {agmAgendas.map((agenda) => {
              const forPct = Math.round((agenda.votes_for / agenda.total_votes) * 100) || 0;
              return (
                <div
                  key={agenda.id}
                  className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-teal-100 text-teal-800 font-mono">
                        {agenda.agenda_no}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm">{agenda.title}</h4>
                    </div>

                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        agenda.status === 'passed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {agenda.status === 'passed' ? 'มติผ่านการอนุมัติ ✅' : 'กำลังเปิดรับคะแนนเสียง 🗳️'}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl">
                    {agenda.description}
                  </p>

                  {/* Voting Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-emerald-700">
                        เห็นชอบ (อนุมัติ): {agenda.votes_for} เสียง ({forPct}%)
                      </span>
                      <span className="text-rose-700">
                        ไม่เห็นชอบ: {agenda.votes_against} เสียง
                      </span>
                      <span className="text-slate-500">
                        งดออกเสียง: {agenda.votes_abstain} เสียง
                      </span>
                    </div>

                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                      <div
                        style={{ width: `${forPct}%` }}
                        className="bg-emerald-500 h-full rounded-l-full"
                        title={`เห็นชอบ ${forPct}%`}
                      />
                      <div
                        style={{ width: `${Math.round((agenda.votes_against / agenda.total_votes) * 100)}%` }}
                        className="bg-rose-500 h-full"
                        title="ไม่เห็นชอบ"
                      />
                      <div
                        style={{ width: `${Math.round((agenda.votes_abstain / agenda.total_votes) * 100)}%` }}
                        className="bg-slate-300 h-full rounded-r-full"
                        title="งดออกเสียง"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <BoardAndReporting />
      )}
    </div>
  );
};
